'use client';

import Link from 'next/link';
import { useAuth } from '@/features/auth/auth.hooks';
import { NAV_ACTIONS } from '@/features/marketing/data/navigation.data';
import { UI } from '@/features/marketing/components/_styles';

// Heights in px: layouts that position around the button (e.g. pricing rail) read these.
export const GET_STARTED_HEIGHT = { default: 40, sm: 32 } as const;

// Knob sits 2px inside the button in both states (square closed, full width open).
const SIZE = {
  default: { root: 'h-10 rounded-none text-[13px] [--knob:2.25rem]', width: 'w-[142px]', knob: 'rounded-none', arrow: 14 },
  sm: { root: 'h-8 rounded-none text-[12px] [--knob:1.75rem]', width: 'w-auto', knob: 'rounded-none', arrow: 10 },
} as const;

// The knob is the inverse of the button, so the filled state reads as the same button flipped.
const VARIANT = {
  dark: { root: `${UI.dark} text-white`, knob: 'bg-white text-black' },
  light: { root: 'bg-white text-black', knob: 'bg-black text-white' },
} as const;

// Retro double chevron on an 11×7 pixel grid: two 2px-thick heads, no shaft.
const CHEVRON_ROWS = [0, 1, 2, 3, 2, 1, 0];
const CHEVRON_PIXELS = [0, 6].flatMap((dx) => CHEVRON_ROWS.map((x, y) => ({ x: dx + x, y })));

function PixelChevrons({ height }: { height: number }) {
  return (
    <svg
      height={height}
      width={(height * 11) / 7}
      viewBox="0 0 11 7"
      fill="currentColor"
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      {CHEVRON_PIXELS.map(({ x, y }) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={2} height={1} />
      ))}
    </svg>
  );
}

export interface EchoGetStartedButtonProps {
  href?: string;
  variant?: keyof typeof VARIANT;
  size?: keyof typeof SIZE;
  fullWidth?: boolean;
  className?: string;
  label?: string;
  hoverLabel?: string;
}

export function EchoGetStartedButton({
  href,
  variant = 'dark',
  size = 'default',
  fullWidth = false,
  className = '',
  label,
  hoverLabel,
}: EchoGetStartedButtonProps) {
  const { isAuthorized } = useAuth();
  const action = isAuthorized ? NAV_ACTIONS.dashboard : NAV_ACTIONS.signup;
  const text = label ?? action.label;
  const hoverText = hoverLabel ?? label ?? action.hoverLabel;
  const s = SIZE[size];
  const v = VARIANT[variant];

  return (
    <Link
      href={href ?? action.href}
      className={`group relative flex items-center overflow-hidden font-medium shadow-xs ${
        fullWidth ? 'w-full' : s.width
      } ${s.root} ${v.root} ${className}`}
    >
      <span
        className={`absolute z-10 left-0.5 inset-y-0.5 w-(--knob) group-hover:w-[calc(100%-0.25rem)] group-focus-visible:w-[calc(100%-0.25rem)] transition-[width] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none overflow-hidden flex items-center ${s.knob} ${v.knob}`}
      >
        {/* Centred on the whole button, not on the space left of the chevrons. */}
        <span
          aria-hidden="true"
          className="absolute inset-0 grid place-items-center whitespace-nowrap font-semibold opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-300 delay-100 motion-reduce:transition-none"
        >
          {hoverText}
        </span>
        {/* Idle: chevrons run centre → right and fade out at the edge. Open: they stop, centred. */}
        <span className="ml-auto size-(--knob) shrink-0 overflow-hidden [mask-image:linear-gradient(90deg,#000_50%,transparent_92%)] group-hover:[mask-image:none] group-focus-visible:[mask-image:none]">
          <span className="w-full h-full grid place-items-center animate-chevron-scroll group-hover:animate-none group-focus-visible:animate-none motion-reduce:animate-none">
            <PixelChevrons height={s.arrow} />
          </span>
        </span>
      </span>
      <span className="w-full text-center whitespace-nowrap pl-[calc(var(--knob)+0.5rem)] pr-2">{text}</span>
    </Link>
  );
}
